import { Injectable, signal } from '@angular/core';

export type ToastType = 'success' | 'error' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  createdAt: number;
  durationMs: number;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  private toastsSignal = signal<Toast[]>([]);
  toasts = this.toastsSignal.asReadonly();

  private messageOnly(msg: string): string {
    return String(msg || '').replace(/^HTTP \d+ [^-]* - \s*/i, '').trim() || 'Something went wrong.';
  }

  show(type: ToastType, message: string, opts?: { title?: string; durationMs?: number }): string {
    const toast: Toast = {
      id: crypto?.randomUUID ? crypto.randomUUID() : String(Date.now()) + '_' + Math.random().toString(16).slice(2),
      type,
      title: opts?.title,
      message: this.messageOnly(message),
      createdAt: Date.now(),
      durationMs: Math.max(1500, opts?.durationMs ?? 3500),
    };

    this.toastsSignal.update((arr) => [toast, ...arr].slice(0, 5));
    setTimeout(() => this.dismiss(toast.id), toast.durationMs);
    return toast.id;
  }

  success(message: string, title = 'Success'): string {
    return this.show('success', message, { title });
  }

  error(message: string, title = 'Something went wrong'): string {
    return this.show('error', message, { title, durationMs: 4500 });
  }

  info(message: string, title = 'Info'): string {
    return this.show('info', message, { title });
  }

  dismiss(id: string): void {
    this.toastsSignal.update((arr) => arr.filter((t) => t.id !== id));
  }

  clear(): void {
    this.toastsSignal.set([]);
  }
}

